import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { HiMenuAlt3, HiX } from "react-icons/hi";
import {
  FaFacebookF,
  // FaTwitter,
  FaInstagram,
  FaLinkedinIn,
  FaYoutube,
  FaEnvelope,
  FaPhoneAlt,
} from "react-icons/fa";
import Button from "../Button/Button";
import LazyImage from "../LazyImage/LazyImage";
import styles from "./Header.module.css";
import { ifillip_logo as logo } from "../../controller/Image";

const NAV_ITEMS = [
  { label: "Home", to: "/" },
  { label: "Why iFillip", to: "/Why-iFillip" },
  { label: "Pricing", to: "/pricing" },
  { label: "AI Suite", to: "/ai-suite" },
  { label: "Core Features", to: "/core-features" },
  // { label: "Company", to: "/company" },
  { label: "Blog", to: "/Blog" },
  { label: "Contact", to: "/contact" },
  { label: "Login", to: "/login" },
];

const SOCIAL_LINKS = [
  {
    icon: <FaInstagram />,
    url: "https://www.instagram.com/ifillip_education/",
    label: "Instagram",
  },
  {
    icon: <FaFacebookF />,
    url: "https://www.facebook.com/ifillipportal/",
    label: "Facebook",
  },
  {
    icon: <FaYoutube />,
    url: "https://www.youtube.com/@ifillip ",
    label: "YouTube",
  },
  {
    icon: <FaLinkedinIn />,
    url: "https://www.linkedin.com/products/bharatclouds-ifillip/",
    label: "LinkedIn",
  },
];

const CONTACT_EMAIL = "support@ifillip.com";
const CONTACT_PHONE = "+91 93447 72393";

const Header = ({ ctaLabel = "Book a Demo", ctaTo = "/book-demo" }) => {
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const lastScrollY = useRef(0);
  const location = useLocation();

  const resolvedCta =
    location.pathname === "/book-demo"
      ? { label: "Book a Demo", to: "/book-demo" }
      : { label: ctaLabel, to: ctaTo };

  useEffect(() => {
    const onScroll = () => {
      const currentScrollY = window.scrollY;
      const viewportHeight = window.innerHeight;

      setScrolled(currentScrollY > 8);

      if (currentScrollY < viewportHeight) {
        setHidden(false);
      } else {
        if (currentScrollY > lastScrollY.current && currentScrollY > 100) {
          setHidden(true);
        } else if (currentScrollY < lastScrollY.current) {
          setHidden(false);
        }
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const shouldHide = hidden && !mobileOpen;

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{
        y: shouldHide ? "-100%" : 0,
        opacity: 1,
      }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className={`${styles.header} ${scrolled ? styles.scrolled : ""}`}
    >
      {/* Top Bar */}
      <div className={styles.topBar}>
        <div className={`${styles.topBarInner} page-container`}>
          <div className={styles.socialIcons}>
            {SOCIAL_LINKS.map((social, idx) => (
              <a
                key={idx}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={social.label}
                className={styles.socialLink}
              >
                {social.icon}
              </a>
            ))}
          </div>

          <div className={styles.contactInfo}>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className={styles.contactItem}
              aria-label="Email us"
            >
              <FaEnvelope className={styles.contactIcon} />
              <span>{CONTACT_EMAIL}</span>
            </a>
            <span className={styles.divider} aria-hidden="true">
              |
            </span>
            <a
              href={`tel:${CONTACT_PHONE.replace(/\s/g, "")}`}
              className={styles.contactItem}
              aria-label="Call us"
            >
              <FaPhoneAlt className={styles.contactIcon} />
              <span>{CONTACT_PHONE}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main Header Row */}
      <div className={`${styles.inner} page-container`}>
        <Link
          to="/Why-iFillip"
          className={styles.logo}
          aria-label="iFillip home"
        >
          <LazyImage
            {...logo}
            alt="iFillip - AI-Powered School Management Software"
            wrapperClassName="logoImg"
          />
        </Link>
        <nav className={styles.nav} aria-label="Primary">
          <ul className={styles.navList}>
            {NAV_ITEMS.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={({ isActive }) =>
                    `${styles.navLink} ${isActive ? styles.navLinkActive : ""}`
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className={styles.ctaWrap}>
          <Button as={Link} to={resolvedCta.to} variant="primary" size="sm">
            {resolvedCta.label}
          </Button>
        </div>
        <button
          type="button"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((v) => !v)}
          className={styles.burger}
        >
          {mobileOpen ? <HiX size={24} /> : <HiMenuAlt3 size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className={styles.mobilePanel}
          >
            <ul className={styles.mobileList}>
              {NAV_ITEMS.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    className={({ isActive }) =>
                      `${styles.mobileLink} ${isActive ? styles.mobileLinkActive : ""}`
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
              <li>
                <Button
                  as={Link}
                  to={resolvedCta.to}
                  variant="primary"
                  size="sm"
                  fullWidth
                >
                  {resolvedCta.label}
                </Button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Header;
